import React from "react";
import { Grid, IconButton, Stack } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { useNavigate } from "react-router-dom";
import TableComponent from "../../components/table/list.jsx";
import { CustomTooltip } from "../../consts.js";
import { DATE_FORMAT, PLAYLIST_FILTERS_OPTIONS } from "../../helpers/constant.js";
import { format } from "date-fns";
import { useThemeMode } from "../../components/theme/index.jsx";
import { getConversationListAPI } from "../../services/conversation.js";

function Playlists() {
    const navigate = useNavigate();
    const [data, setData] = React.useState([]);
    const [spinner, setSpinner] = React.useState(false);
    const { colours } = useThemeMode();
    const [pageInfo, setPageInfo] = React.useState({
        currentPage: 0,
        rowsPerPage: 10,
        nextPage: null,
        bookmark: PLAYLIST_FILTERS_OPTIONS[0],
    });
    const [totalConversations, setTotalConversations] = React.useState(0);

    async function getData(rowsPerPage = pageInfo.rowsPerPage, nextPage = pageInfo.nextPage, bookmark = pageInfo.bookmark) {
        try {
            setSpinner(true);
            const _data = await getConversationListAPI({
                campaignId: null,
                archive: false,
                limit: rowsPerPage,
                lastKey: nextPage,
                bookmark: bookmark === PLAYLIST_FILTERS_OPTIONS[0] ? 'all' : bookmark,
            });
            let newData = [..._data.items];
            if (nextPage) {
                newData = [...data, ..._data.items];
            }
            setData([...newData]);
            setTotalConversations(_data.totalConversations);
            setPageInfo((prev) => ({ ...prev, nextPage: _data.lastKey || null }));
            setSpinner(false); // Update the state with all items
        } catch (error) {
            console.error("Error fetching campaign data: ", error);
            setSpinner(false);
        }
    }

    React.useEffect(() => {
        setSpinner(true);
        getData();
    }, []);

    // define table headers
    const columns = React.useMemo(() => ([
        {
            label: "File",
            key: "file",
            sortable: true,
            renderItem: (row) => (<Stack
                direction="row"
                alignItems="center"
                gap={1}
            >
                <CustomTooltip title={"View Details"}>
                    <IconButton color="primary"
                        onClick={() => navigate("/conversation?id=" + row.id)}
                        sx={colours.shadowMd}
                        aria-label="Play"
                    >
                        <FeatherIcon icon="play" />
                    </IconButton>
                </CustomTooltip>
                {row.file || "-"}
            </Stack>)
        },
        {
            label: "Agent",
            key: "agent",
            sortable: true,
            renderItem: (row) => row.agent || "-",
        },
        {
            label: "Campaign",
            key: "campaignName",
            sortable: true,
            renderItem: (row) => (<>
                <div
                    style={{
                        fontWeight: 500,
                    }}
                >
                    {row?.campaignName || "-"}
                </div>
                <div>
                    {row.company}
                </div>
            </>)
        },
        {
            label: "Conversation Date",
            key: "startdate",
            sortable: true,
            renderItem: (row) => (<>
                {format(new Date(row.startdate), DATE_FORMAT) || "-"}
            </>)
        },
        {
            label: "Sentiment",
            key: "sentiment",
            sortable: true,
            renderItem: (row) => (<>
                {row.sentiment === "positive" ? (
                    <div style={{ color: colours.green100 }}>
                        Positive
                    </div>
                ) : null}
                {row.sentiment === "negative" ? (
                    <div style={{ color: colours.critical100 }}>
                        Negative
                    </div>
                ) : null}
                {row.sentiment === "neutral" ? (
                    <div style={{ color: colours.black }}>Neutral</div>
                ) : null}
                {row.sentiment === null ? (
                    <div style={{ color: colours.black }}>Neutral</div>
                ) : null}
            </>)
        },
    ]), [data, colours]);

    const handlePageChange = (_pageInfo) => {
        let nextPage = pageInfo.nextPage;
        if (_pageInfo.REFRESH_DATA) {
            nextPage = null
        }
        setPageInfo({
            ...pageInfo,
            nextPage,
            currentPage: _pageInfo.currentPage,
            rowsPerPage: _pageInfo.rowsPerPage
        });
        if (nextPage || _pageInfo.REFRESH_DATA) {
            getData(_pageInfo.rowsPerPage, nextPage);
        }
    }

    const handleFilter = (filterData) => {
        const newPageInfo = {
            ...pageInfo,
            currentPage: 0,
            nextPage: null,
            bookmark: filterData.bookmarks || PLAYLIST_FILTERS_OPTIONS[0],
        };
        setPageInfo(newPageInfo);
        getData(newPageInfo.rowsPerPage, null, filterData.bookmarks);
    }

    return (
        <Grid display="flex" flexDirection="column" flexGrow="1" overflow="auto">
            <Grid
                className="vocalysed-home-upadted campaigns"
                container
                spacing={0}
                direction="column"
                flexWrap="nowrap"
                flexGrow={1}
                paddingTop={2}
                paddingX={3}
                paddingBottom={3}
                overflow="auto"
            >
                <TableComponent
                    spinner={spinner}
                    data={data}
                    columns={columns}
                    pagination={true}
                    serverPaginate={true}
                    currentPage={pageInfo.currentPage}
                    hasNextPage={pageInfo.nextPage !== null}
                    totalCount={totalConversations}
                    searchKeys={["file", "agent", "campaignName"]}
                    noDataMessage={"No playlist found."}
                    filterTypes={{ playListBookmark: "bookmarks" }}
                    defaultFilterValues={{ playListBookmark: pageInfo.bookmark }}
                    defaultOptions={{ rowPerPage: pageInfo.rowsPerPage }}
                    rowClick={(item) => navigate("/conversation?id=" + item.id)}
                    onPageChange={handlePageChange}
                    onFilter={handleFilter}
                />
            </Grid>
        </Grid>
    );
}

export default Playlists;
